<script setup lang="ts">
import type { ResourceRole } from '@/backend/types'
import {
  useWorkspaceMembers,
  type WorkspaceMember,
} from '@/modules/WorkspaceSettings/useWorkspaceMembers'
import { isEmailValid } from '@/shared/utils/email'
import { useFuzzySearch } from '@/sharedComposables/useFuzzySearch'
import AvatarIcon from '@/uiKit/AvatarIcon.vue'
import BadgeItem from '@/uiKit/BadgeItem.vue'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import ListMenuItem from '@/uiKit/ListMenuItem.vue'
import * as combobox from '@zag-js/combobox'
import { normalizeProps, useMachine } from '@zag-js/vue'

import type { AssignableRole, ResourceMember } from '@/modules/IdentityAndAccess/types'

import ListMenuContainer from '@/uiKit/ListMenuContainer.vue'
import Select from '@/uiKit/Select'
import { useElementSize } from '@vueuse/core'
import { computed, ref, useId } from 'vue'
import type { ProjectMemberRole } from './types'

const props = defineProps<{
  /** All current workspace members */
  members: ResourceMember[]
  /** All roles that the user is able to invite */
  allowedRoles: Extract<ResourceRole, 'reader' | 'editor'>[]
}>()

const emit = defineEmits<{
  invite: [userIds: string[], role: AssignableRole]
}>()

const workspaceMembersStore = useWorkspaceMembers()

const roleTitleMap: Record<ProjectMemberRole, string> = {
  editor: 'Can edit',
  reader: 'Can view',
}

const getRoleTitle = (id: WorkspaceMember['id']): string | undefined => {
  const role = props.members.find((member) => member.id === id)?.role
  if (role !== 'editor' && role !== 'reader') {
    return
  }

  return roleTitleMap[role].toLowerCase()
}

type ListItem = { title: string; description: string; role: ProjectMemberRole }
const listItems = computed<ListItem[]>(() => {
  const items: ListItem[] = []

  if (props.allowedRoles.includes('editor')) {
    items.push({
      role: 'editor',
      title: roleTitleMap.editor,
      description: 'Can access the agent and modify properties or data.',
    })
  }

  if (props.allowedRoles.includes('reader')) {
    items.push({
      role: 'reader',
      title: roleTitleMap.reader,
      description: 'Can access the agent but cannot modify properties or data.',
    })
  }

  return items
})

const defaultRole = props.allowedRoles.includes('editor') ? 'editor' : 'reader'
const selectedRole = ref<ProjectMemberRole>(defaultRole)

const textAndDropdowownTrigger = ref<HTMLElement | null>(null)
const { width: dropdownWidth } = useElementSize(textAndDropdowownTrigger)

const selectedUserIds = ref<string[]>([])
const selectedUsers = computed(() =>
  selectedUserIds.value.map(workspaceMembersStore.getMember).filter((m) => !!m),
)

const onUnselectMember = (id: string) => {
  selectedUserIds.value = selectedUserIds.value.filter((m) => m !== id)
}

const textValue = ref('')
const textValueIsEmail = computed(() => isEmailValid(textValue.value))

const filteredWorkspaceMembers = useFuzzySearch({
  items: computed(() =>
    workspaceMembersStore.workspaceMembers.filter(
      (workspaceMember) => !props.members.some((member) => member.id === workspaceMember.id),
    ),
  ),
  keys: ['email', 'fullName'],
  searchTerm: textValue,
})

const onTextInputBackspace = () => {
  if (textValue.value) {
    return
  }

  selectedUserIds.value.pop()
}

const newUserEmails = ref<string[]>([])

const onSubmitForm = () => {
  emit('invite', selectedUserIds.value, selectedRole.value)
  selectedUserIds.value = []
  selectedRole.value = defaultRole
  textValue.value = ''
}

/** For a11y - the HTML ID of the dropdown listbox */
const LISTBOX_ID = 'workspace-member-listbox'

const collectionRef = computed(() =>
  combobox.collection({
    items: filteredWorkspaceMembers.value,
    itemToValue: (item) => item.id,
    itemToString: (item) => item.fullName || item.email || item.id,
  }),
)

const isOpen = ref(false)
const id = useId()
const service = useMachine(
  combobox.machine,
  computed(() => ({
    id: id,
    get collection() {
      return collectionRef.value
    },
    open: isOpen.value,
    onOpenChange({ open }) {
      isOpen.value = open
    },
    onSelect({ itemValue }) {
      selectedUserIds.value.push(itemValue)
      textValue.value = ''
    },
    inputBehavior: 'autohighlight' as const,
    inputValue: textValue.value,
    value: selectedUserIds.value,
    positioning: {
      offset: {
        crossAxis: 0,
        mainAxis: 4,
      },
    },
  })),
)

const comboboxApi = computed(() => combobox.connect(service, normalizeProps))

/** For a11y - create a HTML ID for each workspace member's dropdown option */
const getItemLabelId = (item: ResourceMember) => `workspace-member-option-${item.id}`

defineExpose({
  newUserEmails,
})
</script>

<template>
  <form
    class="flex flex-col items-stretch justify-stretch gap-2"
    v-bind="comboboxApi.getRootProps()"
    @submit.prevent="onSubmitForm"
  >
    <label
      class="text-md-13px-default text-text"
      v-bind="comboboxApi.getLabelProps()"
      >Invite collaborators</label
    >
    <div class="flex items-stretch justify-stretch gap-2">
      <div
        ref="textAndDropdowownTrigger"
        class="flex min-w-0 grow items-start gap-0.5 rounded-lg bg-background-gray-subtlest p-0.5"
      >
        <div
          v-bind="comboboxApi.getControlProps()"
          class="flex min-h-7 grow flex-wrap items-center gap-1 rounded-corner-8 focus-within:bg-background-transparent-hovered hover:[&:not(:disabled)]:bg-background-transparent-hovered"
          :class="
            comboboxApi.open ? 'bg-background-transparent-hovered' : 'bg-background-transparent'
          "
        >
          <div
            v-if="selectedUserIds.length + newUserEmails.length > 0"
            class="flex flex-wrap items-center gap-1 px-1 pt-1"
          >
            <BadgeItem
              v-for="user in selectedUsers"
              :key="user.id"
              variant="neutral"
              size="sm"
              :label="workspaceMembersStore.getMemberNameFromId(user.id)"
              trailing-icon="close"
              @trailing-icon-click="onUnselectMember(user.id)"
            >
              <template #leading-icon>
                <AvatarIcon
                  :full-text="workspaceMembersStore.getMemberNameFromId(user.id)"
                  shape="circle"
                  size="2xs"
                  :url="user.profilePictureUrl"
                />
              </template>
            </BadgeItem>
            <BadgeItem
              v-for="email in newUserEmails"
              :key="email"
              variant="neutral"
              size="sm"
              :label="email"
              trailing-icon="close"
              @trailing-icon-click="newUserEmails = newUserEmails.filter((e) => e !== email)"
            >
              <template #leading-icon>
                <div
                  class="m-0.5 flex size-3 items-center justify-center rounded-full bg-background-gray-subtlest"
                >
                  <IconSprite
                    icon="user-2"
                    size="xxxs"
                  />
                </div>
              </template>
            </BadgeItem>
          </div>
          <input
            v-bind="comboboxApi.getInputProps()"
            v-model="textValue"
            placeholder="Add people, emails..."
            class="min-w-6 grow rounded-corner-8 bg-background-transparent px-1.5 py-1 outline-none"
            @keydown.backspace.exact="onTextInputBackspace"
          />
        </div>
        <div v-bind="comboboxApi.getPositionerProps()">
          <ListMenuContainer
            v-if="isOpen"
            v-bind="comboboxApi.getContentProps()"
            :style="{ width: dropdownWidth + 'px' }"
            :list-box-attrs="{ id: LISTBOX_ID }"
            class="go-scrollbar max-h-96 overflow-y-auto p-0.5"
            :create-enabled-when-empty="textValueIsEmail && !newUserEmails.includes(textValue)"
            :create-enabled="textValueIsEmail && !newUserEmails.includes(textValue)"
          >
            <ListMenuItem
              v-for="member in filteredWorkspaceMembers"
              :key="member.id"
              class="h-10 min-h-10 px-2 py-1"
              v-bind="comboboxApi.getItemProps({ item: member })"
              :active="comboboxApi.highlightedValue === member.id"
              :aria-labelledby="`${getItemLabelId(member)}`"
            >
              <template #prefix>
                <AvatarIcon
                  :full-text="`${member.fullName}`"
                  shape="circle"
                  class="aspect-square min-w-5"
                  size="sm"
                  :url="member.profilePictureUrl"
                />
              </template>
              <div
                :id="getItemLabelId(member)"
                class="px-1"
              >
                <div class="text-sm-12px-default text-text">
                  {{ member.fullName }}
                </div>
                <div class="text-xs-11px-light text-text-subtle">
                  {{ member.email }}
                </div>
              </div>
              <template #suffix>
                <div class="min-w-max grow text-right text-xs-11px-default text-text-subtlest">
                  {{ getRoleTitle(member.id) }}
                </div>
              </template>
            </ListMenuItem>
            <ListMenuItem
              v-if="filteredWorkspaceMembers.length === 0"
              label="No users found"
              default-hover-disabled
              class="text-text-subtlest"
            />
          </ListMenuContainer>
        </div>
        <Select
          :value="selectedRole"
          :items="listItems.map((data) => ({ value: data.role, label: data.title, data }))"
          disable-teleport
          class="lowercase"
          inline
          @change="selectedRole = $event"
        >
          <template #item="{ item, api }">
            <ListMenuItem
              :active="api.highlightedValue === item.value"
              class="flex !w-[280px] items-start"
              :role="null"
            >
              <template #prefix>
                <div class="flex size-5 min-w-5 items-center justify-center">
                  <IconSprite
                    v-if="selectedRole === item.data.role"
                    class="text-icon-subtle"
                    icon="check"
                  />
                </div>
              </template>
              <div class="px-1 py-0.5">
                <div>{{ item.data.title }}</div>
                <div class="text-text-subtle">
                  {{ item.data.description }}
                </div>
              </div>
            </ListMenuItem>
          </template>
        </Select>
      </div>

      <DarwinButton
        variant="black"
        size="md"
        rounded
        type="submit"
        class="w-[60px]"
      >
        Invite
      </DarwinButton>
    </div>
  </form>
</template>
